import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, MapPin, Package, Truck, Receipt, ChevronDown, ChevronUp } from "lucide-react";

const InfoRow = ({ label, value, testId }) => {
  if (!value) return null;
  return (
    <div className="flex justify-between py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-800 text-right max-w-[60%]" data-testid={testId}>{value}</span>
    </div>
  );
};

const AddressBlock = ({ title, address, icon: Icon }) => {
  if (!address) return null;
  const parts = [address.street, address.city, address.state, address.postal_code || address.pincode].filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{title}</span>
      </div>
      <p className="text-sm text-slate-700">{parts.join(', ')}</p>
    </div>
  );
};

export const LRCopyInfo = ({ data }) => {
  const [expanded, setExpanded] = useState(false);

  if (!data || Object.keys(data).length === 0) return null;

  return (
    <Card className="border-0 shadow-lg" data-testid="lr-copy-info-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="w-4 h-4 text-teal-600" />
          LR Copy Information
          <Badge className="bg-slate-100 text-slate-600 border-0 text-xs ml-auto">
            {data.partner_name || 'Partner'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Always visible: Key fields */}
        <div className="bg-white border border-slate-100 rounded-lg px-3">
          <InfoRow label="Shipper's Ref No." value={data.shippers_reference_no} testId="lr-shippers-ref" />
          <InfoRow label="Total Invoice Value" value={data.total_invoice_value ? `₹${data.total_invoice_value}` : null} testId="lr-invoice-value" />
        </div>

        {/* Always visible: Weight & Boxes */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Package className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Weight & Boxes</span>
          </div>
          <div className="bg-white border border-slate-100 rounded-lg px-3">
            <InfoRow label="Total Boxes" value={data.total_number_of_boxes} testId="lr-total-boxes" />
            <InfoRow label="Actual Weight" value={data.actual_weight_kg ? `${data.actual_weight_kg} kg` : null} testId="lr-actual-weight" />
            {data.boxes_dimensions && data.boxes_dimensions.length > 0 && (
              <div className="py-1.5">
                <span className="text-sm text-slate-500">Box Dimensions (LxWxH cm)</span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {data.boxes_dimensions.map((box, i) => (
                    <Badge key={i} variant="outline" className="text-xs font-mono" data-testid={`lr-box-dim-${i}`}>
                      {box.count}x {box.dimensions_cm}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Expandable: Other details */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center gap-1.5 py-1.5 text-sm text-teal-600 hover:text-teal-700 transition-colors"
          data-testid="lr-info-toggle"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Hide Details
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              View All Details
            </>
          )}
        </button>

        {expanded && (
          <div className="space-y-4">
            {/* Shipment Details */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Receipt className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Shipment Details</span>
              </div>
              <div className="bg-white border border-slate-100 rounded-lg px-3">
                <InfoRow label="Invoice No." value={data.invoice_no} testId="lr-invoice-no" />
                <InfoRow label="Master AWB" value={data.master_awb} testId="lr-master-awb" />
                <InfoRow label="MOT" value={data.mot} testId="lr-mot" />
                <InfoRow label="Freight Payment" value={data.freight_payment} testId="lr-freight-payment" />
                <InfoRow label="Invoice Value Payment" value={data.invoice_value_payment} testId="lr-inv-value-payment" />
                <InfoRow label="Printed On" value={data.printed_on} testId="lr-printed-on" />
              </div>
            </div>

            {/* Addresses */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <AddressBlock title="Picked From" address={data.pickup_address} icon={Truck} />
              <AddressBlock title="Delivered To" address={data.delivery_address} icon={MapPin} />
            </div>

            {/* Shipper / Recipient Names */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.shipper_name && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Shipper</span>
                  <p className="text-sm font-medium text-slate-800 mt-0.5" data-testid="lr-shipper-name">{data.shipper_name}</p>
                </div>
              )}
              {data.recipient_name && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Recipient</span>
                  <p className="text-sm font-medium text-slate-800 mt-0.5" data-testid="lr-recipient-name">{data.recipient_name}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
